import React from 'react';

import { Box, useTheme } from '@chakra-ui/react';
import OKRCreationPrimaryPanelV2 from 'components/RabbitOKRCreationPanel/PrimaryPanel/V2/OKRCreationPrimaryPanelV2';
import { useSelector } from 'react-redux';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';

import { isMobileView } from 'constants/isMobileView';

import { secondaryDrawerWidth } from 'uiToolkitV1/BottomDrawer';

import { ChangeDetailsPanel } from '../PrimaryPanel/Sections/ChangeDetails/ChangeDetails';
import { SECONDARY_PANEL_TYPES } from '../krDrawerSecondaryPanelSlice';
// eslint-disable-next-line import/no-cycle
import AddEditCustomTagsPanel from './AddEditCustomTagsPanel';
import CreateOKRInitiativePanel from './CreateOKRInitiativePanel/CreateOKRInitiativePanel';
import { CreateOKRPanel } from './CreateOKRPanel/CreateOKRPanel';
import { FullHistoryPanel } from './FullHistoryPanel/FullHistoryPanel';
import { OtherDetailsPanel } from './OtherDetailsPanel/OtherDetailsPanel';
import { ParentOKRSelectionPanel } from './ParentOKRSelectionPanel/ParentOKRSelectionPanel';
import { ParentOKRSelectionV2 } from './ParentOKRSelectionPanel/ParentOKRSelectionV2';
import { RealignPanel } from './RealignPanel';
import { CreatePluginMetricPanel } from './ThirdPartyPluginPanel/PluginPanel';
import TitleAndMetricsSuggestionPanel from './TitleAndMetricsSuggestionPanel/TitleAndMetricsSuggestionPanel';

export const SecondaryPanel = ({
  localChangedNameOKR,
  isOpenAsParent = false,
  renderWithoutWrapper = false,
  localSelectedTimePeriod,
  setLocalSelectedTimePeriod,
  selectedParent,
  setSelectedParent,
  isOKRCreationPanel = false,
}) => {
  const theme = useTheme();

  const secondaryPanelDetailsObject = useSelector(kRDrawerSecondaryPanelSelector);

  // type will depend parentState for Left side secondary panel
  const { type, details: secondaryPanelDetails = null } = isOpenAsParent
    ? secondaryPanelDetailsObject?.details?.parentState || {}
    : secondaryPanelDetailsObject || {};

  const isRealignPanel = [
    SECONDARY_PANEL_TYPES.REALIGN_OKR,
    SECONDARY_PANEL_TYPES.REALIGN_CHILDREN,
    SECONDARY_PANEL_TYPES.REALIGN_OKR_WITHOUT_CHILDREN_REALIGN_CONFIRMATION_MODAL,
    SECONDARY_PANEL_TYPES.REALIGN_CHILDREN_AND_DELETE,
  ].includes(type);

  const isParentOKRSelectionPanel = type === SECONDARY_PANEL_TYPES.SELECT_PARENT_OKR;

  const isCreateOKRPanel = type === SECONDARY_PANEL_TYPES.OPEN_CREATE_OKR_PANEL;

  const isCreateOKRSimplePanel = type === SECONDARY_PANEL_TYPES.OPEN_CREATE_OKR_SIMPLE_PANEL;

  const isInitiativeCreatePanel = type === SECONDARY_PANEL_TYPES.CREATE_INITIATIVE;

  const isAddEditCustomTagsPanel = type === SECONDARY_PANEL_TYPES.ADD_EDIT_CUSTOM_TAGS;

  const isCreateCustomMetricPanel = type === SECONDARY_PANEL_TYPES.CREATE_CUSTOM_METRIC;

  const isTitleAndMetricsSuggestionPanel = type === SECONDARY_PANEL_TYPES.TITLE_AND_METRICS_SUGGESTION_PANEL;

  const isOtherDetailsPanel = type === SECONDARY_PANEL_TYPES.OTHER_DETAILS_PANEL;

  const isChangeDetailsPanel = type === SECONDARY_PANEL_TYPES.CHANGE_DETAILS_PANEL;
  const isFullHistoryPanel = type === SECONDARY_PANEL_TYPES.FULL_HISTORY_PANEL;

  const backgroundStyle = { backgroundColor: theme.colors.neutral.EBEFF3 };

  if (renderWithoutWrapper) {
    return (
      <>
        {isRealignPanel ? <RealignPanel /> : null}
        {isParentOKRSelectionPanel ? (
          <ParentOKRSelectionV2
            localSelectedTimePeriod={localSelectedTimePeriod}
            setLocalSelectedTimePeriod={setLocalSelectedTimePeriod}
            selectedParent={selectedParent}
            setSelectedParent={setSelectedParent}
          />
        ) : null}
        {isCreateOKRPanel ? (
          <CreateOKRPanel isOpenAsParent={isOpenAsParent} secondaryPanelDetails={secondaryPanelDetails} />
        ) : null}
        {isInitiativeCreatePanel ? <CreateOKRInitiativePanel /> : null}
        {isAddEditCustomTagsPanel ? <AddEditCustomTagsPanel /> : null}
        {isCreateCustomMetricPanel ? <CreatePluginMetricPanel /> : null}
        {isTitleAndMetricsSuggestionPanel ? (
          <TitleAndMetricsSuggestionPanel
            secondaryPanelDetails={secondaryPanelDetails}
            localChangedNameOKR={localChangedNameOKR}
            isOpenAsParent={isOpenAsParent}
            isAlignmentDisabled={secondaryPanelDetails?.isAlignmentDisabled}
          />
        ) : null}
        {isOtherDetailsPanel ? (
          <OtherDetailsPanel secondaryPanelDetails={secondaryPanelDetails} isOpenAsParent={isOpenAsParent} />
        ) : null}
      </>
    );
  }

  return (
    <Box
      h="100%"
      minW={isMobileView() ? '100%' : secondaryDrawerWidth}
      maxW={isMobileView() || isOpenAsParent ? '100%' : secondaryDrawerWidth}
      flexGrow={isOpenAsParent ? 1 : undefined}
      borderRadius="0px 8px 8px 0px"
      overflow="hidden"
      p={isOpenAsParent && isOKRCreationPanel ? 0 : 1}
      pl={0}
      opacity={isOpenAsParent ? 0.5 : 1}
      pointerEvents={isOpenAsParent ? 'none' : 'unset'}
      {...backgroundStyle}
    >
      {isRealignPanel ? <RealignPanel /> : null}
      {isParentOKRSelectionPanel ? <ParentOKRSelectionPanel /> : null}
      {isCreateOKRPanel ? <CreateOKRPanel isOpenAsParent={isOpenAsParent} /> : null}
      {isCreateOKRSimplePanel ? (
        <OKRCreationPrimaryPanelV2
          isSecondaryPanel
          onClose={() => {}}
          showSecondaryPanel={false}
          onObjectiveCreation={() => {}}
          onKeyResultCreation={() => {}}
        />
      ) : null}
      {isInitiativeCreatePanel ? <CreateOKRInitiativePanel /> : null}
      {isAddEditCustomTagsPanel ? <AddEditCustomTagsPanel /> : null}
      {isCreateCustomMetricPanel ? <CreatePluginMetricPanel /> : null}
      {isTitleAndMetricsSuggestionPanel ? (
        <TitleAndMetricsSuggestionPanel
          secondaryPanelDetails={secondaryPanelDetails}
          localChangedNameOKR={localChangedNameOKR}
          isOpenAsParent={isOpenAsParent}
          isAlignmentDisabled={secondaryPanelDetails?.isAlignmentDisabled}
        />
      ) : null}
      {isOtherDetailsPanel ? (
        <OtherDetailsPanel secondaryPanelDetails={secondaryPanelDetails} isOpenAsParent={isOpenAsParent} />
      ) : null}
      {isChangeDetailsPanel ? (
        <ChangeDetailsPanel secondaryPanelDetails={secondaryPanelDetails} isOpenAsParent={isOpenAsParent} />
      ) : null}
      {isFullHistoryPanel ? (
        <FullHistoryPanel secondaryPanelDetails={secondaryPanelDetails} isOpenAsParent={isOpenAsParent} />
      ) : null}
    </Box>
  );
};
